import { IAuthenticatedAccount, IRegion, IUser } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { omit } from 'lodash';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import hydratedOrganization from '@alberta/konexi-shared/dist/auth/hydrated-organization';
import { environment } from 'src/environments/environment.default';
import { readAccountId, validateAccount } from './account/account-validation';
import { storageDiagnostics, StorageDiagnosticsService } from './storage-diagnostics/storage-diagnostics.service';

const CURRENT_USER_KEY = 'currentUser';

/**
 * The signed-in user's account (#26615): the in-memory value, its copy in Ionic Storage for the next
 * offline start, and the few derived answers the app asks for often.
 *
 * Value and copy are written together and the copy always after the value, so the running process
 * can never hold the staler one. A copy that goes missing while the app runs is therefore a storage
 * problem, not an authentication problem — and re-reading a copy of something already in memory
 * must not be able to end the session.
 *
 * Whether the user is signed in, and signing in and out, is the SessionController's; this class only
 * knows the account the controller gave it. Callers that need a field read it from the account:
 * `(await requireAccount('caller')).firstName`.
 */
@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly _account$ = new BehaviorSubject<IAuthenticatedAccount | null>(null);
  /** Set by clear() and never reset — see getAccount(). */
  private _hasLoggedOut = false;

  public readonly account$: Observable<IAuthenticatedAccount | null> = this._account$.asObservable();

  constructor(private readonly _storage: Storage) {}

  //#region the account

  public get current(): IAuthenticatedAccount | null {
    return this._account$.value;
  }

  public get hasAccount(): boolean {
    return this._account$.value != null;
  }

  /** Resolves with the next available account: immediately while one is loaded, otherwise with the first one a login delivers. */
  public get accountReady(): Promise<IAuthenticatedAccount> {
    return firstValueFrom(this._account$.pipe(filter((account): account is IAuthenticatedAccount => account != null)));
  }

  /**
   * The current account, or null if not authenticated.
   *
   * Waits for the account only until the first login of this process. After a logout callers get
   * their null right away (log lines, timers, views being torn down) instead of a promise that only
   * settles with the next login.
   */
  public async getAccount(): Promise<IAuthenticatedAccount | null> {
    if (this._account$.value == null && !this._hasLoggedOut) {
      await this.accountReady;
    }
    return this._account$.value;
  }

  /**
   * The account for callers that cannot work without one. Asking for account data while there is
   * no account is a programming error, and a null or default answer would travel on into
   * `createdBy` fields, history entries, list filters and the SQLCipher key derivation — so the call
   * rejects with a message that names the cause.
   */
  public async requireAccount(caller: string): Promise<IAuthenticatedAccount> {
    const account = await this.getAccount();
    if (!account) {
      throw new Error(`[26615] ${caller}: no account available`);
    }
    return account;
  }

  //#endregion

  //#region written by the session controller

  /**
   * Takes the account the backend returned after a successful socket authentication.
   *
   * A complete account replaces the cache. An incomplete one (incident 24139: a backend glitch can
   * deliver a user without authorization) must not overwrite the last known good copy, otherwise a
   * temporary glitch turns into a forced logout on the next offline start — the cache is kept
   * unless it clearly belongs to a different user. The in-memory account is updated either way, so
   * the running session behaves as the backend says.
   */
  public async applyBackendUser(user: unknown): Promise<IAuthenticatedAccount> {
    const validation = validateAccount(user, 'user');
    window.logger.addBreadcrumb({
      category: 'auth',
      // never dereference an object that was just reported as broken (#26615)
      message: `account received from the backend, userId=${readAccountId(user)} valid=${validation.isValid}`,
    });
    if (validation.isValid) {
      await this.write(user as IAuthenticatedAccount, 'backend login');
    } else {
      window.logger.error(
        'user from auth is not valid',
        new Error(`user from auth result is not valid: ${validation.reason}`)
      );
      await this.keepOrDropCached(user);
    }
    const account = user as IAuthenticatedAccount;
    this._account$.next(account);
    return account;
  }

  /**
   * Loads the cached account for a start without backend. Returns null when there is none; the
   * controller decides whether that is worth a Sentry issue (it knows whether a token exists).
   */
  public async loadCached(): Promise<IAuthenticatedAccount | null> {
    const cached = await this._storage.get(CURRENT_USER_KEY);
    const validation = validateAccount(cached, 'currentUser');
    if (validation.isValid) {
      this._account$.next(cached);
      return cached;
    }

    const inMemory = this._account$.value;
    this.reportCacheMiss(validation.reason, inMemory != null);
    return inMemory;
  }

  /** The e-mail of the last cached account, without loading it; null when there is none. */
  public async cachedEmail(): Promise<string | null> {
    const cached = (await this._storage.get(CURRENT_USER_KEY)) as { email?: string } | null;
    return cached?.email ?? null;
  }

  /** Drops the account and its cache; getAccount() answers null from now on instead of waiting. */
  public async clear(): Promise<void> {
    // memory first: a failing storage must not leave the account of the ended session in place
    this._hasLoggedOut = true;
    this._account$.next(null);
    await this._storage.remove(CURRENT_USER_KEY);
  }

  //#endregion

  //#region derived answers

  public async getCurrentUserId(): Promise<string> {
    const account = await this.requireAccount('getCurrentUserId');
    return account._id;
  }

  public async getUserFullName(): Promise<string> {
    const account = await this.requireAccount('getUserFullName');
    return `${account.firstName} ${account.lastName}`;
  }

  public async getUserEmail(): Promise<string> {
    const account = await this.getAccount();
    return account?.email;
  }

  public async isAgent(): Promise<boolean> {
    const account = await this.requireAccount('isAgent');
    return account.isAgent === true;
  }

  public async userIsItLabs(): Promise<boolean> {
    const account = await this.requireAccount('userIsItLabs');
    return (
      environment.validITLabsUser.some(mailOrSuffixToCheck => account.email.includes(mailOrSuffixToCheck)) ||
      (account.canChangeChannel != null && Boolean(account.canChangeChannel))
    );
  }

  public async canCreateTenant(): Promise<boolean> {
    const account = await this.requireAccount('canCreateTenant');
    return environment.validITLabsUser.some(mailOrSuffixToCheck => account.email.includes(mailOrSuffixToCheck));
  }

  public async getCalendarConnector(): Promise<string> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.calendarConnector;
  }

  public async canUseAlbertaLight(): Promise<boolean> {
    const account = await this.getAccount();
    return account?.authorization?.hydratedOrganization?.alberta?.canUseAlbertaLight === true;
  }

  public async getCurrentUserGroups(): Promise<string[] | null> {
    const account = await this.getAccount();
    if (!account?.groups) {
      const errormessage = `{user groups not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.groups;
  }

  public async getCurrentUserRegions(): Promise<IRegion[] | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.regions) {
      const errormessage = `{user regions not found on auth object}`;
      window.logger.error(errormessage, new Error(errormessage));
      return null;
    }
    return account.authorization.regions;
  }

  /**
   * Returns the tenantId of the current user in the organization property.
   * CAREFUL: THIS IS THE NAME OF THE TENANT, NOT THE ID!
   * EXAMPLE: '1TLabs'
   */
  public async getCurrentUserTenantName(): Promise<string | null> {
    const account = await this.getAccount();
    if (!account?.authorization?.hydratedOrganization?.tenantId) {
      window.logger.error('getCurrentUserTenantId failed', new Error('getCurrentUserTenantId failed'));
      return null;
    }
    return account.authorization.hydratedOrganization.tenantId;
  }

  public async getCurrentUserOrganization(): Promise<hydratedOrganization | null> {
    const account = await this.requireAccount('getCurrentUserOrganization');
    return account.authorization?.hydratedOrganization;
  }

  public async getUserPermission(domain: string): Promise<number> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return 0;
    }
    const rightSet = account.authorization.rightSets.find(auth => auth.domain === domain);
    return rightSet ? rightSet.permission : 0;
  }

  public async userHasPermission(domain: string, permission: number): Promise<boolean> {
    const account = await this.getAccount();
    if (!account?.authorization?.rightSets) {
      return false;
    }
    return account.authorization.rightSets.some(
      rightSet => rightSet.permission >= permission && rightSet.domain === domain
    );
  }

  //#endregion

  //#region mutations

  public async setO365Id(o365Id: string): Promise<void> {
    const account = await this.getAccount();
    if (!account) {
      // getAccount() can legitimately resolve to null (after a logout it no longer waits), and
      // dereferencing it here threw an unhandled TypeError (#26615).
      window.logger.error('setO365Id without account', new Error('[26615] setO365Id: account is null'));
      return;
    }
    account.o365Id = o365Id;
    await this.update(account, 'setO365Id');
  }

  public async resetRights(rights): Promise<void> {
    if (!rights) {
      return;
    }
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }
    account.authorization.rightSets = rights;
    await this.update(account, 'resetRights');
  }

  public async setRegions(regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }
    account.authorization.regions = regions;
    await this.update(account, 'setRegions');
  }

  public async resetCurrentUser(user: IUser, regions: IRegion[]): Promise<void> {
    const account = await this.getAccount();
    if (!account?.authorization) {
      return;
    }
    // Spreading the synced IUser over the account used to be able to replace `authorization`
    // wholesale — the users collection carries no hydratedOrganization, so the cached account
    // silently lost it and the next offline start forced a logout (#26615). `authorization`
    // is therefore excluded from the merge and updated explicitly below.
    const updatedAccount: IAuthenticatedAccount = {
      ...account,
      ...omit(user, ['organization', 'authorization']),
      authorization: { ...account.authorization, regions },
    };
    await this.update(updatedAccount, 'resetCurrentUser');
  }

  /** Publishes a mutated account and persists it; an invalid one is published but not persisted. */
  public async update(account: IAuthenticatedAccount, origin: string): Promise<void> {
    this._account$.next(account);
    const validation = validateAccount(account, 'account');
    if (!validation.isValid) {
      window.logger.error(
        `${origin} refused to persist an invalid account`,
        new Error(`[26615] ${origin}: ${validation.reason}`)
      );
      return;
    }
    await this.write(account, origin);
  }

  //#endregion

  //#region storage

  private async write(account: IAuthenticatedAccount, origin: string): Promise<void> {
    try {
      await this._storage.set(CURRENT_USER_KEY, account);
    } catch (error) {
      window.logger.error(`${origin} failed to store currentUser`, error);
    }
  }

  private async keepOrDropCached(invalidAccount: unknown): Promise<void> {
    try {
      const cachedId = readAccountId(await this._storage.get(CURRENT_USER_KEY));
      const incomingId = readAccountId(invalidAccount);
      if (cachedId != null && incomingId != null && cachedId !== incomingId) {
        await this._storage.remove(CURRENT_USER_KEY);
        window.logger.addBreadcrumb({
          category: 'auth',
          message: '[26615] invalid account for a different user — cached account dropped',
        });
        return;
      }
      window.logger.addBreadcrumb({
        category: 'auth',
        message: `[26615] invalid account not written, cached account kept (cached=${cachedId != null})`,
      });
    } catch (error) {
      window.logger.error('keepOrDropCachedAccount failed', error);
    }
  }

  /**
   * Reports that `currentUser` was missing or invalid in Ionic Storage (#26615). "Session kept from
   * memory" is a Sentry issue on purpose: it is the countable near-miss of a storage that loses
   * data while the app runs. The storage generation verdict tells an evicted storage apart from a
   * fresh install.
   */
  private reportCacheMiss(reason: string, sessionKeptFromMemory: boolean): void {
    try {
      const storageState = StorageDiagnosticsService.describe();
      window.logger.addBreadcrumb({
        category: 'auth',
        message: `currentUser cache miss | reason=${reason} keptFromMemory=${sessionKeptFromMemory} ${storageState}`,
        data: {
          reason,
          sessionKeptFromMemory,
          storageVerdict: storageDiagnostics.verdict,
          storageDriver: storageDiagnostics.driver,
        },
      });
      if (sessionKeptFromMemory) {
        window.logger.error(
          'authenticateOffline cache miss, session kept from memory',
          new Error(
            `[26615] currentUser lost from storage while the app was running, session continued from memory | reason=${reason} | ${storageState}`
          )
        );
      }
    } catch {
      // diagnostics only
    }
  }

  //#endregion
}
